"use client";

import React, { useState, useMemo } from "react";
import Image from "next/image";
import { 
  Activity, 
  Sparkles, 
  Sliders, 
  Gauge, 
  Award, 
  Flame, 
  Heart, 
  Zap, 
  CheckCircle2, 
  Info,
  ChevronRight
} from "lucide-react";

export function AthleticScienceExplorer() {
  const [activeStation, setActiveStation] = useState<number>(0);

  // Interactive Calculator State
  const [age, setAge] = useState<number>(13.5);
  const [standingHeight, setStandingHeight] = useState<number>(158);
  const [sittingHeight, setSittingHeight] = useState<number>(82);
  const [weight, setWeight] = useState<number>(48);
  const [sprintTime, setSprintTime] = useState<number>(4.22);
  const [broadJump, setBroadJump] = useState<number>(195);
  const [gender, setGender] = useState<"boy" | "girl">("boy");

  // Stations specifications
  const stations = [
    {
      name: "30m Flying Sprint",
      metric: "Maximum Speed & Anaerobic Acceleration",
      protocol: "Dual-beam laser timing gates measure pure acceleration and terminal velocity across 30 meters.",
      proxy: "Type-IIx Fast-Twitch Muscle Fiber Recruitment",
      image: "/athletic-testing/sprint.jpg",
      accent: "text-amber-400 border-amber-500/40 bg-amber-500/10",
      color: "#F59E0B"
    },
    {
      name: "10x5m Shuttle Run",
      metric: "Agility & Deceleration Mechanics",
      protocol: "Athletes sprint back-and-forth between 5m lines executing 9 rapid decelerations and directional pivots.",
      proxy: "Dynamic Neuromuscular Balance & Core Braking",
      image: "/athletic-testing/shuttle.jpg",
      accent: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
      color: "#10B981"
    },
    {
      name: "Standing Broad Jump",
      metric: "Lower-Body Explosive Elastic Power",
      protocol: "Two-footed stationary horizontal leap measuring displacement in centimeters.",
      proxy: "Stretch-Shortening Cycle & Explosive Hip Drive",
      image: "/athletic-testing/broad-jump.jpg",
      accent: "text-rose-400 border-rose-500/40 bg-rose-500/10",
      color: "#F43F5E"
    },
    {
      name: "20m Beep Test (PACER)",
      metric: "Aerobic Capacity & VO2 Max",
      protocol: "Progressive multi-stage shuttle run paced against acoustic beeps until volitional exhaustion.",
      proxy: "Cardiorespiratory Endurance (ml/kg/min)",
      image: "/athletic-testing/beep-test.jpg",
      accent: "text-cyan-400 border-cyan-500/40 bg-cyan-500/10",
      color: "#06B6D4"
    },
    {
      name: "Sit-and-Reach Test",
      metric: "Hamstring & Lumbar Spine Flexibility",
      protocol: "Standardized sliding-scale box measuring posterior chain and spinal flexibility.",
      proxy: "Musculoskeletal Elasticity & Injury Prevention",
      image: "/athletic-testing/sit-reach.jpg",
      accent: "text-purple-400 border-purple-500/40 bg-purple-500/10",
      color: "#8B5CF6"
    },
    {
      name: "Somatic Screening & Ape Index",
      metric: "Biological Proportions & Growth Stage",
      protocol: "Precision measurement of Standing Height, Sitting Height, Arm Span, and Body Mass.",
      proxy: "Mirwald PHV Maturity Offset & Ape Index",
      image: "/athletic-testing/somatic.jpg",
      accent: "text-blue-400 border-blue-500/40 bg-blue-500/10",
      color: "#3B82F6"
    }
  ];

  // Mirwald Algorithm Calculation (Real sports science formula)
  const calculationResults = useMemo(() => {
    const legLength = standingHeight - sittingHeight;
    const ratio = (standingHeight / weight) * 100;

    let offset = 0;
    if (gender === "boy") {
      offset =
        -9.236 +
        0.0002708 * (legLength * sittingHeight) -
        0.001663 * (age * legLength) +
        0.007216 * (age * sittingHeight) +
        0.02292 * ratio;
    } else {
      offset =
        -9.376 +
        0.0001882 * (legLength * sittingHeight) +
        0.0022 * (age * sittingHeight) +
        0.05841 * ratio;
    }

    const roundedOffset = Number(offset.toFixed(2));
    let stage: "PRE_PHV" | "CIRCA_PHV" | "POST_PHV" = "CIRCA_PHV";
    let stageDescription = "";

    if (roundedOffset < -1.0) {
      stage = "PRE_PHV";
      stageDescription = "Pre-Growth Spurt: Focus on motor coordination, speed mechanics, and agility drills. Heavy weights contraindicated.";
    } else if (roundedOffset <= 1.0) {
      stage = "CIRCA_PHV";
      stageDescription = "Peak Height Velocity Spurt: Maximum growth window. High vulnerability in growth plates. Focus on flexibility, posture & aerobic baseline.";
    } else {
      stage = "POST_PHV";
      stageDescription = "Post-Growth Spurt: High circulating hormonal levels allow maximum power development, hypertrophy, and sport-specific specialization.";
    }

    // Sprint Percentile approximation
    const sprintScore = Math.max(10, Math.min(99, Math.round(99 - (sprintTime - 3.9) * 45)));
    const powerScore = Math.max(10, Math.min(99, Math.round(((broadJump - 130) / 100) * 80 + 20)));

    // Talent Suitability Index (TSI)
    const trackSuitability = Math.round((sprintScore * 0.6 + powerScore * 0.4));
    const footballSuitability = Math.round((sprintScore * 0.5 + powerScore * 0.3 + 20));

    return {
      offset: roundedOffset,
      legLength,
      stage,
      stageDescription,
      sprintScore,
      powerScore,
      trackSuitability,
      footballSuitability
    };
  }, [age, standingHeight, sittingHeight, weight, sprintTime, broadJump, gender]);

  return (
    <section id="science" className="py-24 border-b border-[#3A3A38]/20 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 space-y-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 border border-[#1A3C2B]/20 bg-[#1A3C2B]/5 px-3 py-1 font-mono-tech text-[10px] uppercase tracking-widest text-[#1A3C2B]">
            <span>02. OLYMPIC BIOMETRICS &amp; EUROFIT PROTOCOLS</span>
          </div>
          <h2 className="font-space font-bold text-4xl sm:text-6xl text-[#1A3C2B] tracking-tight uppercase leading-[0.95]">
            SCIENTIFIC ATHLETIC TESTING SUITE.
          </h2>
          <p className="font-mono-tech text-xs text-[#3A3A38] uppercase tracking-wider leading-relaxed">
            REPLACING SUBJECTIVE COACHING OPINIONS WITH STANDARDIZED INTERNATIONAL TESTING BATTERIES AND PEDIATRIC PHYSIOLOGICAL ALGORITHMS (EUROFIT, SAI, MIRWALD PHV).
          </p>
        </div>

        {/* 6 Stations Explorer Tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-8">
          {stations.map((st, idx) => (
            <button
              key={st.name}
              onClick={() => setActiveStation(idx)}
              className={`p-3 rounded-xl border text-left transition-all font-mono text-xs ${
                activeStation === idx
                  ? "bg-slate-800 border-amber-500/60 shadow-[0_0_15px_rgba(245,158,11,0.25)]"
                  : "bg-slate-900/40 border-white/5 text-slate-400 hover:border-white/20 hover:text-white"
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-slate-500">STATION 0{idx + 1}</span>
                {activeStation === idx && <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
              </div>
              <div className="font-outfit font-bold text-white text-xs sm:text-sm truncate">
                {st.name}
              </div>
            </button>
          ))}
        </div>

        {/* Active Station Card */}
        <div className="bg-gradient-to-br from-slate-900/90 via-[#0F172A] to-slate-950 border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Station Image (5 cols) */}
            <div className="lg:col-span-5 relative rounded-2xl overflow-hidden aspect-[4/3] border border-white/10 shadow-xl group">
              <Image
                src={stations[activeStation].image}
                alt={stations[activeStation].name}
                fill
                className="object-cover group-hover:scale-105 transition-transform duration-500"
                priority
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="px-2.5 py-1 rounded-full text-[10px] font-mono font-bold bg-amber-500/90 text-slate-950 uppercase">
                  Station 0{activeStation + 1}
                </span>
                <h4 className="font-outfit text-xl font-bold text-white mt-1">
                  {stations[activeStation].name}
                </h4>
              </div>
            </div>

            {/* Station Details (7 cols) */}
            <div className="lg:col-span-7 space-y-5 text-left">
              <div>
                <span className="text-xs font-mono text-amber-400 uppercase tracking-wider block">
                  Measured Biomarker
                </span>
                <h3 className="font-outfit text-2xl sm:text-3xl font-bold text-white">
                  {stations[activeStation].metric}
                </h3>
              </div>

              <div className="p-4 rounded-xl bg-slate-900/80 border border-white/5 space-y-2">
                <div className="text-xs font-mono text-slate-400 flex items-center gap-1.5 font-bold uppercase">
                  <Info className="w-3.5 h-3.5 text-cyan-400" /> Standardized Protocol
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {stations[activeStation].protocol}
                </p>
              </div>

              <div className="p-4 rounded-xl bg-slate-900/80 border border-white/5 space-y-2">
                <div className="text-xs font-mono text-slate-400 flex items-center gap-1.5 font-bold uppercase">
                  <Award className="w-3.5 h-3.5 text-emerald-400" /> Physiological Significance
                </div>
                <p className="text-sm text-emerald-300/90 leading-relaxed">
                  {stations[activeStation].proxy}
                </p>
              </div>

              <div className="pt-1 flex items-center gap-4 text-xs font-mono text-slate-400">
                <span>Standard: Eurofit &amp; SAI</span>
                <span>•</span>
                <span>Testing Duration: ~90 secs</span>
                <span>•</span>
                <span>Grade Band: 5 to 12</span>
              </div>
            </div>
          </div>
        </div>

        {/* Interactive Live Mirwald Somatic Calculator */}
        <div className="bg-[#0F172A] border border-purple-500/30 rounded-3xl p-6 sm:p-10 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <Sliders className="w-72 h-72 text-purple-400" />
          </div>

          <div className="relative z-10 space-y-8">
            {/* Header */}
            <div className="max-w-2xl space-y-2 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono font-bold tracking-wider bg-purple-500/15 text-purple-300 border border-purple-500/30">
                <Sparkles className="w-3.5 h-3.5" />
                INTERACTIVE DEMO: MIRWALD SOMATIC CALCULATOR
              </div>
              <h3 className="font-outfit text-2xl sm:text-3xl font-black text-white">
                Experience the Biological Maturity Offset Engine.
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Adjust the student sliders below to see how Olympio eliminates the Relative Age Effect (puberty bias) by calculating Peak Height Velocity (PHV) offset in real time.
              </p>
            </div>

            {/* Slider Controls & Live Output */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Sliders (7 cols) */}
              <div className="lg:col-span-7 space-y-5 bg-slate-900/80 p-6 rounded-2xl border border-white/5">
                {/* Gender Toggle */}
                <div className="flex items-center justify-between border-b border-white/5 pb-4">
                  <span className="text-xs font-mono text-slate-400">Student Biological Profile:</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setGender("boy")}
                      className={`px-3 py-1 rounded-lg text-xs font-mono font-bold transition-colors ${
                        gender === "boy" ? "bg-amber-500 text-slate-950" : "bg-slate-800 text-slate-400"
                      }`}
                    >
                      Male Cohort
                    </button>
                    <button
                      onClick={() => setGender("girl")}
                      className={`px-3 py-1 rounded-lg text-xs font-mono font-bold transition-colors ${
                        gender === "girl" ? "bg-purple-500 text-slate-950" : "bg-slate-800 text-slate-400"
                      }`}
                    >
                      Female Cohort
                    </button>
                  </div>
                </div>

                {/* Age Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-slate-300">Chronological Age:</span>
                    <span className="text-amber-400 font-bold">{age} Years</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={17.5}
                    step={0.1}
                    value={age}
                    onChange={e => setAge(parseFloat(e.target.value))}
                    className="w-full accent-amber-500 bg-slate-800 h-2 rounded-lg cursor-pointer"
                  />
                </div>

                {/* Standing Height Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-slate-300">Standing Height:</span>
                    <span className="text-cyan-400 font-bold">{standingHeight} cm</span>
                  </div>
                  <input
                    type="range"
                    min={130}
                    max={195}
                    step={1}
                    value={standingHeight}
                    onChange={e => setStandingHeight(parseInt(e.target.value))}
                    className="w-full accent-cyan-500 bg-slate-800 h-2 rounded-lg cursor-pointer"
                  />
                </div>

                {/* Sitting Height Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-slate-300">Sitting Height (Trunk Length):</span>
                    <span className="text-purple-400 font-bold">{sittingHeight} cm</span>
                  </div>
                  <input
                    type="range"
                    min={65}
                    max={105}
                    step={1}
                    value={sittingHeight}
                    onChange={e => setSittingHeight(parseInt(e.target.value))}
                    className="w-full accent-purple-500 bg-slate-800 h-2 rounded-lg cursor-pointer"
                  />
                </div>

                {/* Weight Slider */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-slate-300">Body Mass:</span>
                    <span className="text-emerald-400 font-bold">{weight} kg</span>
                  </div>
                  <input
                    type="range"
                    min={30}
                    max={90}
                    step={1}
                    value={weight}
                    onChange={e => setWeight(parseInt(e.target.value))}
                    className="w-full accent-emerald-500 bg-slate-800 h-2 rounded-lg cursor-pointer"
                  />
                </div>

                {/* 30m Sprint Time */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-mono">
                    <span className="text-slate-300">30m Sprint Time:</span>
                    <span className="text-rose-400 font-bold">{sprintTime} seconds</span>
                  </div>
                  <input
                    type="range"
                    min={3.8}
                    max={6.0}
                    step={0.02}
                    value={sprintTime}
                    onChange={e => setSprintTime(parseFloat(e.target.value))}
                    className="w-full accent-rose-500 bg-slate-800 h-2 rounded-lg cursor-pointer"
                  />
                </div>
              </div>

              {/* Output Cards (5 cols) */}
              <div className="lg:col-span-5 space-y-4">
                {/* Result 1: Somatic Band */}
                <div className="p-5 rounded-2xl bg-slate-900 border border-purple-500/40 space-y-3 shadow-xl">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-purple-300">PEAK HEIGHT VELOCITY (PHV) OFFSET</span>
                    <span className="px-2 py-0.5 rounded font-bold text-amber-300 bg-amber-500/20 border border-amber-500/30">
                      {calculationResults.offset > 0 ? `+${calculationResults.offset}` : calculationResults.offset} YRS
                    </span>
                  </div>

                  <div className="space-y-1">
                    <div className="text-lg font-outfit font-bold text-white flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-purple-400 animate-pulse" />
                      Biological Category: {calculationResults.stage}
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed font-sans">
                      {calculationResults.stageDescription}
                    </p>
                  </div>

                  <div className="pt-2 border-t border-white/10 flex justify-between text-[11px] font-mono text-slate-400">
                    <span>Leg Length: {calculationResults.legLength} cm</span>
                    <span>Sitting/Height Ratio: {((sittingHeight / standingHeight) * 100).toFixed(1)}%</span>
                  </div>
                </div>

                {/* Result 2: Olympic Talent Index */}
                <div className="p-5 rounded-2xl bg-slate-900 border border-amber-500/40 space-y-3 shadow-xl">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-amber-300">OLYMPIC TALENT SUITABILITY INDEX (TSI)</span>
                    <span className="text-emerald-400 font-bold">
                      {calculationResults.sprintScore}th %ile Speed
                    </span>
                  </div>

                  <div className="space-y-2 font-mono text-xs">
                    <div>
                      <div className="flex justify-between text-slate-300 mb-1">
                        <span>Track Sprinting (100m/200m):</span>
                        <span className="text-amber-400 font-bold">{calculationResults.trackSuitability}% Suitability</span>
                      </div>
                      <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                        <div
                          className="bg-amber-400 h-full rounded-full transition-all duration-300"
                          style={{ width: `${calculationResults.trackSuitability}%` }}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-slate-300 mb-1">
                        <span>Football Winger / Striker:</span>
                        <span className="text-cyan-400 font-bold">{calculationResults.footballSuitability}% Suitability</span>
                      </div>
                      <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
                        <div
                          className="bg-cyan-400 h-full rounded-full transition-all duration-300"
                          style={{ width: `${calculationResults.footballSuitability}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <p className="text-[11px] text-slate-400 pt-1 font-sans">
                    ✨ <strong>Talent Discovery Insight:</strong> This student exhibits top-tier neuromuscular acceleration. Even if they currently appear smaller, their biological trajectory points to varsity track potential.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
